// Корневой компонент клиентского портала.
// Пирамида провайдеров: ThemeProvider → QueryClientProvider → AuthProvider →
// RouterProvider → DistributorBrandProvider.
// (DistributorBrandProvider после S22/S44 фетчит /api/public/distributor-info
// через React Query → должен быть внутри QueryClientProvider.)
import { lazy, Suspense } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { ThemeProvider } from '@dss/design-system'
import { AuthProvider, RequireAuth, useAuthWorkspaceKey } from '@dss/auth'
import { PrivateQueryWorkspace } from '@dss/portal-ui'
import { Link, RouterProvider, useLocation } from './lib/router'
import { DistributorBrandProvider } from './lib/distributor-brand'
import { authConfig } from './lib/auth-config'
import { queryClient } from './lib/queryClient'
import { LoginScreen } from './features/login/LoginScreen'
import { ForgotStep1 } from './features/forgot-password/ForgotStep1'
import { AuthCallback } from './features/auth/AuthCallback'
import { PortalCatalogScreen } from './features/catalog/PortalCatalogScreen'
import { LogoutScreen } from './features/logout/LogoutScreen'
import { LogoutConfirmScreen } from './features/logout/LogoutConfirmScreen'
import { SessionExpiredModal } from './features/session/SessionExpiredModal'
import { RoleGate, isPublicClientRoute } from './components/RoleGate'
import { GroupInvitationScreen, EmployeeInvitationScreen, OwnerInvitationScreen } from './features/invitations/GroupInvitationScreen'
import { ClientInvitationWorkspace } from './features/invitations/ClientInvitationWorkspace'

// MeScreen — единственный screen, который ходит в backend. Lazy чтобы не тащить
// его в bundle для логина.
const MeScreen = lazy(() =>
  import('./features/invitations/PortalHomeScreen').then((m) => ({ default: m.PortalHomeScreen })),
)
const OrderDetailScreen = lazy(() =>
  import('./features/orders/PortalOrdersScreen').then((m) => ({
    default: m.PortalOrdersScreen,
  })),
)
const OrdersListScreen = lazy(() =>
  import('./features/orders/PortalOrdersScreen').then((m) => ({
    default: m.PortalOrdersScreen,
  })),
)
const ProductDetailScreen = lazy(() =>
  import('./features/catalog/PortalCatalogScreen').then((m) => ({
    default: m.PortalCatalogScreen,
  })),
)
const CartScreen = lazy(() =>
  import('./features/cart/CartScreen').then((m) => ({ default: m.CartScreen })),
)

const ORDER_DETAIL_RE = /^\/orders\/(\d+)\/?$/
// D49 — карточка текущего каталога по ID, с серверной проверкой точки и публикации.
const PRODUCT_DETAIL_RE = /^\/catalog\/p\/(\d+)\/?$/

// S26 — расширенный профиль (info + edit + смена пароля + смена email).
const ProfileScreen = lazy(() =>
  import('./features/profile/ProfileScreen').then((m) => ({ default: m.ProfileScreen })),
)

function NotFound() {
  return (
    <div
      style={{
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
      }}
    >
      <h1 style={{ margin: 0, fontSize: 28, fontWeight: 600 }}>404</h1>
      <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>Страница не найдена.</p>
    </div>
  )
}

function RouteSwitch() {
  const { pathname } = useLocation()

  // Auth callback — обработка PKCE redirect; AuthProvider уже работает в bootstrap.
  if (pathname.endsWith('/auth/callback')) {
    return <AuthCallback />
  }

  // Auth-flow страницы — без RequireAuth.
  if (pathname === '/login') return <LoginScreen />
  if (['/forgot-password', '/forgot-password/sent', '/forgot-password/reset',
    '/forgot-password/done'].includes(pathname)) return <ForgotStep1 />
  if (pathname === '/logout') return <LogoutScreen />
  if (pathname === '/group-invite') return <GroupInvitationScreen />
  if (pathname === '/employee-invite') return <EmployeeInvitationScreen />
  if (pathname === '/owner-invite') return <OwnerInvitationScreen />
  if (pathname === '/group-employees') return <RequireAuth><ClientInvitationWorkspace /></RequireAuth>

  // Защищённые страницы.
  if (pathname === '/catalog') {
    return (
      <RequireAuth>
        <PortalCatalogScreen />
      </RequireAuth>
    )
  }
  if (pathname === '/me' || pathname === '/') {
    return (
      <RequireAuth>
        <Suspense
          fallback={
            <div
              role="status"
              aria-live="polite"
              style={{
                minHeight: '60vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--color-text-secondary)',
                fontSize: 13,
              }}
            >
              Загружаем профиль…
            </div>
          }
        >
          <MeScreen />
        </Suspense>
      </RequireAuth>
    )
  }
  if (pathname === '/profile') {
    return (
      <RequireAuth>
        <Suspense
          fallback={
            <div
              role="status"
              aria-live="polite"
              style={{
                minHeight: '60vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--color-text-secondary)',
                fontSize: 13,
              }}
            >
              Загружаем профиль…
            </div>
          }
        >
          <ProfileScreen />
        </Suspense>
      </RequireAuth>
    )
  }
  if (pathname === '/logout-confirm') {
    return (
      <RequireAuth>
        <LogoutConfirmScreen />
      </RequireAuth>
    )
  }
  if (pathname === '/cart' || pathname === '/cart/') {
    return (
      <RequireAuth>
        <Suspense
          fallback={
            <div
              role="status"
              aria-live="polite"
              style={{
                minHeight: '60vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--color-text-secondary)',
                fontSize: 13,
              }}
            >
              Загружаем корзину…
            </div>
          }
        >
          <CartScreen />
        </Suspense>
      </RequireAuth>
    )
  }
  if (pathname === '/orders' || pathname === '/orders/') {
    return (
      <RequireAuth>
        <Suspense
          fallback={
            <div
              role="status"
              aria-live="polite"
              style={{
                minHeight: '60vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--color-text-secondary)',
                fontSize: 13,
              }}
            >
              Загружаем список заказов…
            </div>
          }
        >
          <OrdersListScreen />
        </Suspense>
      </RequireAuth>
    )
  }
  // Прежние страницы не монтируются и не читают сохранённый legacy-контекст.
  if (/^\/me\/(tradepoints|payers|agreements)(\/|$)/.test(pathname)) {
    return <RequireAuth><main className="group-invitation-page">
      <h1>Этот раздел заменён</h1>
      <p>Актуальные торговые точки и разрешённых плательщиков смотрите в рабочем месте клиента.
        Отдельный раздел соглашений в этой версии не предоставляется.</p>
      <Link to="/me">Открыть мои точки и доступ</Link>
    </main></RequireAuth>
  }
  // D49 — карточка товара в текущем доступе клиента.
  const productDetailMatch = PRODUCT_DETAIL_RE.exec(pathname)
  if (productDetailMatch) {
    const productId = Number(productDetailMatch[1])
    if (!Number.isSafeInteger(productId) || productId < 1) return <NotFound />
    return (
      <RequireAuth>
        <Suspense
          fallback={
            <div
              role="status"
              aria-live="polite"
              style={{
                minHeight: '60vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--color-text-secondary)',
                fontSize: 13,
              }}
            >
              Загружаем карточку товара…
            </div>
          }
        >
          <ProductDetailScreen productId={productId} />
        </Suspense>
      </RequireAuth>
    )
  }
  const orderDetailMatch = ORDER_DETAIL_RE.exec(pathname)
  if (orderDetailMatch) {
    const orderId = Number(orderDetailMatch[1])
    return (
      <RequireAuth>
        <Suspense
          fallback={
            <div
              role="status"
              aria-live="polite"
              style={{
                minHeight: '60vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--color-text-secondary)',
                fontSize: 13,
              }}
            >
              Загружаем карточку заказа…
            </div>
          }
        >
          <OrderDetailScreen orderId={orderId} />
        </Suspense>
      </RequireAuth>
    )
  }

  return <NotFound />
}

function GlobalChrome() {
  const scope = useAuthWorkspaceKey()
  const { pathname } = useLocation()
  // Безопасно рендерить SessionExpiredModal только когда AuthProvider активен;
  // он внутри useAuth() уже бросит если контекста нет, поэтому держим его внутри AuthProvider.
  //
  // RoleGate оборачивает RouteSwitch: на каждом не-публичном пути проверяет,
  // что принципал — CLIENT_OWNER/CLIENT_EMPLOYEE. Не-client юзеров редиректит
  // в правильный SPA (BUG-CO-01, P0 SECURITY).
  return (
    <>
      <SessionExpiredModal />
      {isPublicClientRoute(pathname) ? <RouteSwitch /> : <PrivateQueryWorkspace scope={scope}><RoleGate>
        <RouteSwitch />
      </RoleGate></PrivateQueryWorkspace>}
    </>
  )
}

export function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider config={authConfig}>
          <RouterProvider>
            <DistributorBrandProvider>
              <GlobalChrome />
            </DistributorBrandProvider>
          </RouterProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  )
}
